import React, { useState, useContext, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/pt';
import { FiChevronLeft, FiFileText, FiMoreHorizontal, FiTrash2 } from 'react-icons/fi';
import Swal from 'sweetalert2';
import { useSession } from '@/hooks/useSession';
import { ProjectsContext } from './provider';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import toast from 'react-hot-toast';
import Card from '@/components/custom/card/card';
import { EmptyState } from '@/components/custom/empty-state';
import { Dropdown } from '@/components/custom/dropdown/dropdown';
import { CardAvatarStack } from '@/components/ui/card-avatar-stack';
import { ProjectSchedule } from './projects-schedule';
import { Link } from 'react-router-dom';
import usePermission from '@/hooks/usePermission';
import { Loader } from '@/components/custom/loader';
import { useClient } from 'jsonapi-react';
import { useTranslation } from 'react-i18next';
import { ProjectsCardComment } from './projects-card-comment';
import { Button, ButtonBack } from '@/components/ui/button';
import { IconWrapper } from '@/components/custom/icon';
import { Badge } from '@/components/ui/badge';
import { PostActionsButton, PostCardWrapper, PostCommentList } from '@/components/custom/post';
import { Input, InputFile } from '@/components/ui/input';
import { Sheet, SheetBody, SheetCardWrapper, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  ProjectCard,
  ProjectCardFile,
  ProjectCardHeader,
  ProjectCardFileTitle,
  ProjectWorkspaceContent,
  ProjectWorkspaceBody,
  ProjectWorkspaceHeader,
  ProjectWorkspaceHeaderContent,
  ProjectWorkspaceTitle,
  ProjectWorkspaceDateTime,
  ProjectWorkspaceDateTimeWrapper,
  ProjectWorkspacePage,
  ProjectWorkspaceCol,
  ProjectWorkspaceAside,
  ProjectWorkspaceWidget,
  ProjectWorkspaceWidgetHeader,
  ProjectWorkspaceWidgetTitle,
  ProjectWorkspaceWidgetCard,
  ProjectWorkspaceWidgetCardHeader,
  ProjectWorkspaceWidgetCardBody
} from '@/components/custom/project';

export function ProjectsContent() {
  const { t } = useTranslation();
  const client = useClient();
  const [versionCommentDraft, setVersionCommentDraft] = useState('');
  const [showModal, setShowModal] = useState({
    addParticipant: false,
    removeParticipant: false
  });
  const { session } = useSession();
  const id = session.user.id;
  const [allProfiles, setAllProfiles] = useState();
  const [ownProject, setOwnProject] = useState(true);
  const [versionUser, setVersionUser] = useState({});
  const [versionUserLoading, setVersionUserLoading] = useState(true);
  const [comments, setComments] = useState([]);

  const {
    selectedProject,
    selectedProjectVersion,
    // approveProjectVersion,
    confirmApproveProject,
    getAllStudents,
    studentFilter,
    filteredStudents,
    createProjectVersion,
    createProjectVersionComment,
    addStudentToSelectedProject,
    deleteStudentFromSelectedProject,
    deleteProjectVersion,
    loading,
    deleteProjectVersionComment
  } = useContext(ProjectsContext);

  const authorityLevel = useAuthorityLevel(session);

  function checkIfUserIsOnProjec(selectedProject, id) {
    return selectedProject?.users?.some(obj => obj.id === id);
  }

  const getAllProfiles = async () => {
    const { data, error } = await client.fetch('profiles');
    if (error) {
      toast.error(t('toast.errorGetParticipants'));
    } else {
      setAllProfiles(
        data
          .map(item => item.name)
          .reduce((item, i) => {
            return `${item},${i}`;
          })
      );
    }
  };

  useEffect(() => {
    if (selectedProject && authorityLevel === 'student') {
      if (checkIfUserIsOnProjec(selectedProject, id)) {
        setOwnProject(true);
      } else {
        setOwnProject(false);
      }
    }
  }, [selectedProject, session]);

  const destroyStudentForm = student => {
    Swal.fire({
      title: t('projects.warningDeleteStudentFromProject'),
      text: t('projects.warningDeleteCannotBeUndoneText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: t('button.delete'),
      showCloseButton: true
    }).then(result => {
      if (result.isConfirmed) {
        deleteStudentFromSelectedProject(student.id);
        toast.success(t('toast.successRemoveStudentFromProject'));
      }
    });
  };

  const getUser = async () => {
    setVersionUserLoading(true);
    const { data, error } = await client.fetch(`users/${selectedProjectVersion['user_id']}`);
    if (error) {
      toast.error('Erro ao buscar informações de quem enviou esta versão.');
    } else {
      setVersionUser(data);
      setVersionUserLoading(false);
    }
  };

  const fetchComments = async () => {
    const { data, error } = await client.fetch(`/comments?filter[project_version_id]=${selectedProjectVersion?.id}`);

    if (!error) {
      setComments(data);
    } else {
      toast.error('Erro ao buscar comentários');
    }
  };

  const handleDeleteComment = async commentId => {
    Swal.fire({
      title: t('projects.warningDeleteCommentTitle'),
      text: t('projects.warningDeleteCannotBeUndoneText'),
      confirmButtonText: t('button.yes'),
      showDenyButton: true,
      denyButtonText: t('button.no'),
      showCloseButton: true
    }).then(res => {
      if (res.isConfirmed) {
        deleteProjectVersionComment(commentId);
        setComments(prev => prev.filter(item => item.id !== commentId));
      }
    });
  };

  useEffect(() => {
    if (selectedProjectVersion && selectedProjectVersion.user_id !== null) {
      getUser();
    }

    fetchComments();
  }, [selectedProjectVersion]);

  useEffect(() => {
    getAllProfiles();
  }, []);

  const commentsFiltered = comments?.filter(comment => comment?.['comment-parent-id'] === null);

  return ownProject ? (
    <ProjectWorkspaceContent>
      <ProjectWorkspaceBody>
        {selectedProject && (
          <>
            <ProjectWorkspaceHeader>
              <Link to={`/projetos`}>
                <ButtonBack>
                  <IconWrapper>
                    <FiChevronLeft />
                  </IconWrapper>
                </ButtonBack>
              </Link>

              <ProjectWorkspaceHeaderContent>
                {selectedProject.approved ? (
                  <Badge
                    variant="success"
                    size="xs"
                  >
                    {t('exams.statusInApproved')}
                  </Badge>
                ) : (
                  <Badge
                    variant="primary-light"
                    size="xs"
                  >
                    {t('exams.inProgress')}
                  </Badge>
                )}

                <ProjectWorkspaceTitle>{selectedProject.name}</ProjectWorkspaceTitle>
                <ProjectWorkspaceDateTimeWrapper>
                  <ProjectWorkspaceDateTime>
                    {t('projects.createdIn')} {moment(selectedProject['created-at']).format(t('datesForm.formatDate'))}
                  </ProjectWorkspaceDateTime>
                  <Button
                    variant="outline"
                    size="xs"
                    asChild
                  >
                    <Link to={`/projetos/${selectedProject.id}/sobre`}>{t('button.aboutProject')}</Link>
                  </Button>
                </ProjectWorkspaceDateTimeWrapper>
              </ProjectWorkspaceHeaderContent>

              {authorityLevel === 'student' && !selectedProject.approved && (
                <InputFile
                  variant="button"
                  buttonLabel={t('projects.buttonSendNewVersion')}
                  name="newVersion"
                  id="newVersion"
                  accept=".pdf, .doc, .docx, image/*"
                  onChange={e => {
                    const filename = e.target.value;
                    const regex = /^C:\\fakepath\\/;
                    let fileName = filename.replace(regex, '');
                    createProjectVersion(e.target.files, fileName);
                  }}
                  value=""
                />
              )}

              {usePermission('Aprovar projetos', 'Projetos') && selectedProjectVersion && !selectedProject.approved ? (
                <Button
                  type="button"
                  onClick={confirmApproveProject}
                >
                  Aprovar projeto
                </Button>
              ) : null}
            </ProjectWorkspaceHeader>

            <ProjectWorkspacePage>
              <ProjectWorkspaceCol>
                {loading && <Loader />}

                {selectedProjectVersion && !loading && (
                  <PostCardWrapper>
                    <ProjectCard>
                      {!versionUserLoading && (
                        <ProjectCardHeader>
                          <CardAvatarStack data={[versionUser]} />
                          {versionUser.name}
                        </ProjectCardHeader>
                      )}
                      <ProjectCardFile>
                        <IconWrapper>
                          <FiFileText />
                        </IconWrapper>
                        <ProjectCardFileTitle>{selectedProjectVersion?.file?.filename?.toUpperCase()}</ProjectCardFileTitle>
                      </ProjectCardFile>
                      {selectedProjectVersion.file?.url && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                          >
                            <a
                              href={selectedProjectVersion.file?.url}
                              target="_blank"
                              rel="noreferrer noopener"
                              // Linha abaixo comentada, pois não faz um onClick neste elemento
                              // onClick={selectedProject.user.id == session.user.id || authorityLevel === 'admin' ? approveProjectVersion : ''}
                            >
                              {authorityLevel === 'student' ? 'Download' : 'Avaliar'}
                            </a>
                          </Button>

                          {(authorityLevel === 'admin' || selectedProjectVersion.user_id === session.user.id) && !selectedProject.approved && (
                            <PostActionsButton
                              isDanger
                              className="absolute right-4 top-4"
                            >
                              <FiTrash2
                                size={16}
                                onClick={() => {
                                  Swal.fire({
                                    title: t('projects.warningDeleteProjectVersionTitle'),
                                    text: t('projects.warningDeleteCannotBeUndoneText'),
                                    confirmButtonText: t('button.yes'),
                                    showDenyButton: true,
                                    denyButtonText: t('button.no'),
                                    showCloseButton: true
                                  }).then(res => {
                                    if (res.isConfirmed) {
                                      deleteProjectVersion(selectedProjectVersion);
                                    }
                                  });
                                }}
                              />
                            </PostActionsButton>
                          )}
                        </>
                      )}
                    </ProjectCard>

                    {commentsFiltered?.length > 0 && (
                      <PostCommentList>
                        {commentsFiltered.map(c => (
                          <ProjectsCardComment
                            key={c.id}
                            comment={c}
                            comments={comments}
                            onDelete={handleDeleteComment}
                          />
                        ))}
                      </PostCommentList>
                    )}

                    {!selectedProject.approved && usePermission('Postar e comentar em forum', 'Projetos') ? (
                      <div className="w-full">
                        <Input
                          placeholder={t('textsCommon.placeholderSendYourComment')}
                          aria-label={t('textsCommon.placeholderSendYourComment')}
                          value={versionCommentDraft}
                          onChange={e => setVersionCommentDraft(e.target.value)}
                          onKeyPress={e => {
                            if (e.key === 'Enter') {
                              createProjectVersionComment(versionCommentDraft);
                              setVersionCommentDraft('');
                            }
                          }}
                          className="bg-bright"
                        />
                        <p className="mb-0 text-right text-xs text-neutral">{t('textsCommon.textPressEnter')}</p>
                      </div>
                    ) : null}
                  </PostCardWrapper>
                )}

                {!selectedProjectVersion && !loading && (
                  <EmptyState
                    type="data"
                    title={t('emptyState.missingDataTitle')}
                    text={authorityLevel === 'admin' ? t('emptyState.textNoDataYet') : t('emptyState.submittingNewVersionText')}
                  />
                )}
              </ProjectWorkspaceCol>

              <ProjectWorkspaceAside>
                <ProjectWorkspaceWidget>
                  <ProjectWorkspaceWidgetHeader>
                    <ProjectWorkspaceWidgetTitle>{t('projects.titleProjectParticipants')}</ProjectWorkspaceWidgetTitle>
                    {!selectedProject.approved && (usePermission('Adicionar participantes', 'Projetos') || usePermission('Excluir participantes', 'Projetos')) && (
                      <Dropdown>
                        <Dropdown.Toggle>
                          <FiMoreHorizontal />
                        </Dropdown.Toggle>

                        <Dropdown.Content>
                          {usePermission('Adicionar participantes', 'Projetos') && (
                            <Dropdown.Item
                              onClick={() => {
                                getAllStudents(allProfiles);
                                setShowModal({
                                  ...showModal,
                                  addParticipant: true
                                });
                              }}
                            >
                              {t('courses.addParticpants')}
                            </Dropdown.Item>
                          )}

                          {usePermission('Excluir participantes', 'Projetos') && (
                            <Dropdown.Item
                              onClick={() => {
                                getAllStudents();
                                setShowModal({
                                  ...showModal,
                                  removeParticipant: true
                                });
                              }}
                            >
                              {t('courses.deleteParticipants')}
                            </Dropdown.Item>
                          )}
                        </Dropdown.Content>
                      </Dropdown>
                    )}
                  </ProjectWorkspaceWidgetHeader>

                  <ProjectWorkspaceWidgetCard>
                    <ProjectWorkspaceWidgetCardHeader>{t('projects.participants')}</ProjectWorkspaceWidgetCardHeader>
                    <ProjectWorkspaceWidgetCardBody>
                      {selectedProject?.users?.length > 0 ? (
                        <CardAvatarStack
                          data={selectedProject?.users}
                          modalTitle={t('projects.titleProjectParticipants')}
                        />
                      ) : (
                        authorityLevel === 'admin' &&
                        !selectedProject.approved && (
                          <Button
                            variant="link"
                            size="xs"
                            className="font-light text-neutral-dark"
                            onClick={() => {
                              getAllStudents();
                              setShowModal({
                                ...showModal,
                                addParticipant: true
                              });
                            }}
                          >
                            Vamos adicionar estudantes?
                          </Button>
                        )
                      )}
                    </ProjectWorkspaceWidgetCardBody>
                  </ProjectWorkspaceWidgetCard>
                </ProjectWorkspaceWidget>

                <ProjectSchedule selectedProject={selectedProject} />
              </ProjectWorkspaceAside>
            </ProjectWorkspacePage>
          </>
        )}
      </ProjectWorkspaceBody>

      <Sheet
        open={showModal.addParticipant}
        onOpenChange={() => setShowModal({ ...showModal, addParticipant: false })}
      >
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('projects.selectParticipants')}</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <SheetCardWrapper>
              <Input
                className="sticky top-0 z-10"
                type="search"
                placeholder={t('courses.searchParticipants')}
                aria-label={t('courses.searchParticipants')}
                onChange={e => studentFilter(e.target.value)}
              />

              {filteredStudents?.length > 0 &&
                filteredStudents.map(student => (
                  <Card.StudentEmail
                    student={student}
                    profileImage={student.image.url}
                    registered
                    key={student.id}
                    studentId={student.id}
                    name={student.name}
                    studentEmail={student.email}
                    buttonText={t('button.add')}
                    onClick={() => {
                      setShowModal({ ...showModal, addParticipant: false });
                      addStudentToSelectedProject(student.id);
                    }}
                  />
                ))}
            </SheetCardWrapper>
          </SheetBody>
          <SheetFooter />
        </SheetContent>
      </Sheet>

      <Sheet
        open={showModal.removeParticipant}
        onOpenChange={() => setShowModal({ ...showModal, removeParticipant: false })}
      >
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('courses.deleteParticipants')}</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <SheetCardWrapper>
              {selectedProject?.users?.length > 0 ? (
                selectedProject?.users.map(u => (
                  <Card.StudentEmail
                    student={u}
                    registered
                    key={u.id}
                    name={u.name}
                    studentEmail={u.email}
                    buttonText={t('button.remove')}
                    photo={u.image.url}
                    onClick={() => {
                      destroyStudentForm(u);
                    }}
                  />
                ))
              ) : (
                <p className="mb-4">Nenhum participante incluído no projeto</p>
              )}
            </SheetCardWrapper>
          </SheetBody>
        </SheetContent>
        <SheetFooter />
      </Sheet>
    </ProjectWorkspaceContent>
  ) : (
    <ProjectWorkspaceContent>
      <EmptyState
        type="content"
        title={t('emptyState.projectNoAccessTitle')}
        text={t('emptyState.contactATeacherText')}
      />
    </ProjectWorkspaceContent>
  );
}
